import { Clock, Calendar, FileText, Heart, Timer, Smile, CheckCircle2, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './core/Button';
import { Card, CardHeader, CardContent } from './core/Card';
import { useAuth } from '../graphql/hooks/useAuth';

export function UserDashboard() {
  const { userName } = useAuth();
  
  // Current date: Thursday, January 8, 2026
  const currentDate = new Date(2026, 0, 8);

  // Get first name only for personal greeting
  const getFirstName = (fullName: string) => {
    return fullName.split(' ')[0] || fullName;
  };

  // User data
  const userData = {
    firstName: getFirstName(userName || 'Holger'),
    overtime: 16.5,
    vacationDays: 20,
    totalVacationDays: 30,
    pendingRequests: 1,
    missingEntries: 0,
  };

  // Missing time entries
  const missingEntries = [
    {
      date: '2026-01-06',
      dayName: 'Montag',
      displayDate: '06.01.2026',
    },
    {
      date: '2026-01-07',
      dayName: 'Dienstag',
      displayDate: '07.01.2026',
    },
  ];

  // Pending requests
  const pendingRequests = [
    {
      id: '1',
      type: 'Urlaub',
      startDate: '2026-01-20',
      endDate: '2026-01-24',
      days: 5,
      description: 'Winterurlaub',
      submittedDate: '2026-01-08',
    },
  ];

  const getGreeting = () => {
    const hour = currentDate.getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  const getFormattedDate = () => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const dayName = days[currentDate.getDay()];
    const day = currentDate.getDate();
    const month = months[currentDate.getMonth()];
    
    return `${dayName}, ${day}. ${month}`;
  };

  const handleNavigate = (page: string) => {
    const event = new CustomEvent('navigate', {
      detail: { page }
    });
    window.dispatchEvent(event);
  };

  const hasMissingEntries = userData.missingEntries > 0;
  const hasPendingRequests = userData.pendingRequests > 0;
  const hasOpenItems = hasMissingEntries || hasPendingRequests;

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1200px] mx-auto">
        {/* Personal Greeting */}
        <div className="mb-8">
          <h1 className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium leading-tight mb-2">
            {getGreeting()}, {userData.firstName}
          </h1>
          <p className="text-[#6b6b6b] text-[15px]">{getFormattedDate()}</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Overview and Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Section: Dein Überblick */}
            <div>
              <h2 className="text-[#1c1c1c] text-[20px] font-medium mb-4">Ihr aktueller Überblick</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Zeitguthaben */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 20 }}
                  whileHover={{ y: -5, transition: { type: "spring", stiffness: 400, damping: 10 } }}
                >
                  <Card>
                    <CardContent>
                      <div className="flex items-start gap-3">
                        <motion.div 
                          className="size-12 bg-blue-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Clock className="size-6 text-[#0084c7]" />
                        </motion.div>
                        <div className="flex-1">
                          <p className="text-[#6b6b6b] text-[13px] mb-1">Ihr Zeitkonto</p>
                          <motion.p 
                            className="text-[#1c1c1c] text-[26px] font-medium leading-tight mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                          >
                            {userData.overtime > 0 ? '+' : ''}{userData.overtime}h
                          </motion.p>
                          <p className="text-[#6b6b6b] text-[12px] leading-relaxed">
                            Ihr Einsatz wird geschätzt
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Urlaub */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
                  whileHover={{ y: -5, transition: { type: "spring", stiffness: 400, damping: 10 } }}
                >
                  <Card>
                    <CardContent>
                      <div className="flex items-start gap-3">
                        <motion.div 
                          className="size-12 bg-green-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Calendar className="size-6 text-[#16a34a]" />
                        </motion.div>
                        <div className="flex-1">
                          <p className="text-[#6b6b6b] text-[13px] mb-1">Ihr Urlaubskonto</p>
                          <motion.p 
                            className="text-[#1c1c1c] text-[26px] font-medium leading-tight mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                          >
                            {userData.vacationDays}
                          </motion.p>
                          <p className="text-[#6b6b6b] text-[12px] leading-relaxed">
                            Tage stehen Ihnen zu
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Anträge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
                  whileHover={{ y: -5, transition: { type: "spring", stiffness: 400, damping: 10 } }}
                >
                  <Card>
                    <CardContent>
                      <div className="flex items-start gap-3">
                        <motion.div 
                          className="size-12 bg-purple-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <FileText className="size-6 text-purple-600" />
                        </motion.div>
                        <div className="flex-1">
                          <p className="text-[#6b6b6b] text-[13px] mb-1">Ihre Anträge</p>
                          <motion.p 
                            className="text-[#1c1c1c] text-[26px] font-medium leading-tight mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                          >
                            {userData.pendingRequests}
                          </motion.p>
                          <p className="text-[#6b6b6b] text-[12px] leading-relaxed">
                            {userData.pendingRequests === 0 ? 'Alles bearbeitet' : 'In Bearbeitung'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>

            {/* Section: Heute noch offen */}
            {hasOpenItems ? (
              <div>
                <h2 className="text-[#1c1c1c] text-[20px] font-medium mb-4">Handlungsbedarf</h2>
                
                <Card>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Missing Time Entries */}
                      {hasMissingEntries && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-[12px] p-5">
                          <div className="flex items-start gap-4">
                            <div className="size-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                              <Clock className="size-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-2">
                                Zeiteinträge nachtragen
                              </h3>
                              <p className="text-[#6b6b6b] text-[14px] mb-4 leading-relaxed">
                                Damit Ihre Arbeitszeit vollständig erfasst ist, tragen Sie bitte noch folgende Tage nach:
                              </p>
                              <div className="space-y-2 mb-5">
                                {missingEntries.map((entry, index) => (
                                  <div 
                                    key={index}
                                    className="flex items-center gap-3 text-[14px] bg-white rounded-[8px] p-3"
                                  >
                                    <Calendar className="size-5 text-blue-600" />
                                    <span className="text-[#1c1c1c] font-medium">{entry.dayName}</span>
                                    <span className="text-[#6b6b6b]">{entry.displayDate}</span>
                                  </div>
                                ))}
                              </div>
                              <Button
                                variant="primary"
                                size="md"
                                onClick={() => handleNavigate('zeiterfassung')}
                                icon={<Clock className="size-4" />}
                              >
                                Zeiten nachtragen
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pending Requests */}
                      {hasPendingRequests && (
                        <div className="bg-purple-50 border-2 border-purple-200 rounded-[12px] p-5">
                          <div className="flex items-start gap-4">
                            <div className="size-12 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                              <FileText className="size-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-2">
                                {userData.pendingRequests === 1 ? 'Ihr Antrag ist in Bearbeitung' : 'Ihre Anträge sind in Bearbeitung'}
                              </h3>
                              <p className="text-[#6b6b6b] text-[14px] mb-4 leading-relaxed">
                                Wir prüfen {userData.pendingRequests === 1 ? 'Ihren Antrag' : 'Ihre Anträge'} und informieren Sie über die Entscheidung.
                              </p>
                              <div className="space-y-3 mb-5">
                                {pendingRequests.map((request, index) => (
                                  <div 
                                    key={index}
                                    className="bg-white rounded-[10px] p-4"
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="inline-flex px-3 py-1 rounded-full text-[13px] font-medium bg-purple-100 text-purple-700">
                                        {request.type}
                                      </span>
                                      <span className="text-[#6b6b6b] text-[13px]">
                                        {request.days} {request.days === 1 ? 'Tag' : 'Tage'}
                                      </span>
                                    </div>
                                    <p className="text-[#1c1c1c] text-[15px] mb-1">
                                      {new Date(request.startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {new Date(request.endDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </p>
                                    {request.description && (
                                      <p className="text-[#6b6b6b] text-[13px]">
                                        {request.description}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <Button
                                variant="secondary"
                                size="md"
                                onClick={() => handleNavigate('meine-anträge')}
                                icon={<FileText className="size-4" />}
                              >
                                Status ansehen
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Empty State: All Clear */
              <div>
                <h2 className="text-[#1c1c1c] text-[20px] font-medium mb-4">Handlungsbedarf</h2>
                
                <Card>
                  <CardContent>
                    <div className="text-center py-8">
                      <div className="size-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="size-10 text-[#16a34a]" />
                      </div>
                      <h3 className="text-[#1c1c1c] text-[20px] font-medium mb-2">
                        Alles aktuell
                      </h3>
                      <p className="text-[#6b6b6b] text-[15px] max-w-md mx-auto leading-relaxed">
                        Ihre Zeiterfassung ist vollständig. Vielen Dank für Ihre Zuverlässigkeit.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Section: Quick Actions */}
            <div>
              <h2 className="text-[#1c1c1c] text-[20px] font-medium mb-4">Schnellzugriff</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Arbeitszeit erfassen */}
                <motion.button
                  onClick={() => handleNavigate('zeiterfassung')}
                  className="text-left"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    scale: 1.03,
                    y: -5,
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Card className="cursor-pointer hover:border-[#0084c7] hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <motion.div 
                          className="size-14 bg-blue-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.6 }}
                        >
                          <Clock className="size-7 text-[#0084c7]" />
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-1 leading-tight">
                            Arbeitszeit erfassen
                          </h3>
                          <p className="text-[#6b6b6b] text-[13px] leading-relaxed">
                            Zeiten und Einsätze buchen
                          </p>
                        </div>
                        <ChevronRight className="size-5 text-[#6b6b6b] shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.button>

                {/* Urlaub einreichen */}
                <motion.button
                  onClick={() => handleNavigate('meine-anträge')}
                  className="text-left"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    scale: 1.03,
                    y: -5,
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Card className="cursor-pointer hover:border-[#16a34a] hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <motion.div 
                          className="size-14 bg-green-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.6 }}
                        >
                          <Calendar className="size-7 text-[#16a34a]" />
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-1 leading-tight">
                            Urlaub einreichen
                          </h3>
                          <p className="text-[#6b6b6b] text-[13px] leading-relaxed">
                            Freie Tage planen
                          </p>
                        </div>
                        <ChevronRight className="size-5 text-[#6b6b6b] shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.button>

                {/* Krankmeldung */}
                <motion.button
                  onClick={() => handleNavigate('meine-anträge')}
                  className="text-left"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    scale: 1.03,
                    y: -5,
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Card className="cursor-pointer hover:border-red-600 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <motion.div 
                          className="size-14 bg-red-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ scale: [1, 1.2, 1.2, 1], rotate: [0, 0, 10, -10, 0] }}
                          transition={{ duration: 0.6 }}
                        >
                          <Heart className="size-7 text-red-600" />
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-1 leading-tight">
                            Krankmeldung senden
                          </h3>
                          <p className="text-[#6b6b6b] text-[13px] leading-relaxed">
                            Krankheitstage melden
                          </p>
                        </div>
                        <ChevronRight className="size-5 text-[#6b6b6b] shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.button>

                {/* Meine Anfragen */}
                <motion.button
                  onClick={() => handleNavigate('meine-anträge')}
                  className="text-left"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    scale: 1.03,
                    y: -5,
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Card className="cursor-pointer hover:border-purple-600 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <motion.div 
                          className="size-14 bg-purple-50 rounded-[12px] flex items-center justify-center shrink-0"
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.6 }}
                        >
                          <FileText className="size-7 text-purple-600" />
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[#1c1c1c] text-[17px] font-medium mb-1 leading-tight">
                            Meine Anfragen ansehen
                          </h3>
                          <p className="text-[#6b6b6b] text-[13px] leading-relaxed">
                            Status und Verlauf prüfen
                          </p>
                        </div>
                        <ChevronRight className="size-5 text-[#6b6b6b] shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Context and Information */}
          <div className="space-y-6">
            {/* This Week */}
            <Card>
              <CardHeader
                title="Diese Woche"
                borderBottom
              />
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-[#6b6b6b] text-[13px]">Erfasste Stunden</span>
                      <span className="text-[#1c1c1c] text-[22px] font-medium">32.5h</span>
                    </div>
                    <div className="h-2 bg-[#e9e9e9] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#0084c7] rounded-full transition-all"
                        style={{ width: '81%' }}
                      />
                    </div>
                    <p className="text-[#6b6b6b] text-[12px] mt-2">
                      von 40 Soll-Stunden
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Month Overview */}
            <Card>
              <CardHeader
                title="Januar 2026"
                borderBottom
              />
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-3 bg-[#0084c7] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Arbeitstage</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[16px] font-medium">6</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-3 bg-[#16a34a] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Urlaub</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[16px] font-medium">0</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-3 bg-red-500 rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Krank</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[16px] font-medium">1</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Appreciation Card */}
            <Card>
              <CardContent>
                <div className="text-center py-4">
                  <div className="size-14 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Smile className="size-7 text-yellow-600" />
                  </div>
                  <p className="text-[#1c1c1c] text-[15px] leading-relaxed">
                    <strong>Danke für Ihren Einsatz!</strong>
                  </p>
                  <p className="text-[#6b6b6b] text-[13px] mt-2 leading-relaxed">
                    Ihre Arbeit trägt zum Erfolg des Teams bei.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}